import React, { useEffect, useMemo, useState } from "react";
import CustomModal from "../CustomModal";
import ErrorMessage from "../ErrorMessages/ErrorMessage";

export interface ActiveExchangeRate {
  id: string;
  from_currency: string;
  to_currency: string;
  rate: number;
  created_at?: string;
}

interface ExchangeRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeRate: ActiveExchangeRate | null;
  /** Threshold (in percent) beyond which a deviation warning is shown. */
  warningThreshold: number;
  loading: boolean;
  onSave: (rate: number) => void;
}

// Mirrors the backend rule: numeric(18,6), so at most 6 decimal places.
const MAX_DECIMALS = 6;

/** Formats the "Last updated" line, e.g. "Aug 19, 2026 · 10:30 AM". */
const formatUpdatedAt = (value?: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} · ${timePart}`;
};

const ExchangeRateModal: React.FC<ExchangeRateModalProps> = ({
  isOpen,
  onClose,
  activeRate,
  warningThreshold,
  loading,
  onSave,
}) => {
  const [value, setValue] = useState<string>("");
  const [error, setError] = useState<string>("");

  const fromCurrency = activeRate?.from_currency || "USD";
  const toCurrency = activeRate?.to_currency || "AFN";

  // Reset to the current rate each time the modal opens, so an abandoned edit
  // never leaks into the next one.
  useEffect(() => {
    if (isOpen) {
      setValue(activeRate ? String(activeRate.rate) : "");
      setError("");
    }
  }, [isOpen, activeRate]);

  const parsed = value.trim() === "" ? NaN : Number(value);

  // Live deviation from the active rate, recomputed as the admin types. Null
  // when there is no active rate to compare against or the input is not yet a
  // valid number.
  const deviation = useMemo<number | null>(() => {
    if (!activeRate || activeRate.rate === 0) return null;
    if (!Number.isFinite(parsed)) return null;
    return ((parsed - activeRate.rate) / activeRate.rate) * 100;
  }, [parsed, activeRate]);

  const showWarning =
    deviation !== null && Math.abs(deviation) > warningThreshold && parsed !== activeRate?.rate;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    // Allow only digits with a single optional decimal point, so the field
    // cannot hold something the backend will reject.
    if (next !== "" && !/^\d*\.?\d*$/.test(next)) {
      return;
    }
    setValue(next);
    if (error) setError("");
  };

  const validate = (): string => {
    if (value.trim() === "") return "Exchange rate is required";
    if (!Number.isFinite(parsed)) return "Exchange rate must be a valid number";
    if (parsed <= 0) return "Exchange rate must be greater than 0";
    const decimals = value.split(".")[1]?.length ?? 0;
    if (decimals > MAX_DECIMALS) {
      return `Exchange rate must not have more than ${MAX_DECIMALS} decimal places`;
    }
    return "";
  };

  const handleSubmit = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    onSave(parsed);
  };

  const lastUpdated = formatUpdatedAt(activeRate?.created_at);

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Exchange Rate"
      maxWidth="max-w-[440px]"
      showCloseButton={false}
      closeOnOverlayClick={!loading}
      primaryButtonText="Save & Apply"
      secondaryButtonText="Cancel"
      handelSubmit={handleSubmit}
      buttonLoading={loading}
      buttonWidth="w-fit"
      secondaryWidth="w-fit"
      reverseButton="justify-end"
      // The design uses a sectioned card — 16px section padding with full-bleed
      // dividers — rather than CustomModal's uniform p-6, so the padding is
      // stripped here and re-applied per section below.
      className="!p-0 !rounded-2xl"
      titleClassName="text-[18px] font-bold text-primary_text px-4 py-6 border-b border-b-outline"
      footerClassName="px-4 pb-4 !gap-3"
      primaryButtonClassName="!px-6 !rounded-lg"
      secondaryButtonClassName="!px-6 !py-3 !rounded-lg !border-primary_text !text-primary_text"
    >
      <div>
        {/* Current active rate — read-only summary of what is in force today. */}
        <div className="flex flex-col gap-3 p-4">
          <p className="text-[12px] font-semibold capitalize tracking-[0.5px] text-primary_text">
            Current Active Rate
          </p>
          {activeRate ? (
            <>
              <div className="flex items-center gap-3 w-full">
                <div className="flex-1 min-w-0 flex flex-col gap-0.5 bg-[#F8FAFC] border border-outline rounded-xl p-3">
                  <p className="text-[12px] font-semibold leading-normal text-[#72777B]">From</p>
                  <p className="text-[18px] font-bold leading-normal text-primary_text truncate">
                    1 {fromCurrency}
                  </p>
                </div>
                <span
                  className="flex items-center justify-center h-8 w-8 shrink-0 rounded-2xl bg-[#EFF6FF]"
                  aria-hidden="true"
                >
                  <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M2.5 8h11m0 0-4.5-4.5M13.5 8 9 12.5"
                      stroke="#144EA2"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <div className="flex-1 min-w-0 flex flex-col gap-0.5 bg-[#F8FAFC] border border-outline rounded-xl p-3">
                  <p className="text-[12px] font-semibold leading-normal text-[#72777B]">To</p>
                  <p className="text-[18px] font-bold leading-normal text-primary_text truncate">
                    {activeRate.rate} {toCurrency}
                  </p>
                </div>
              </div>
              {lastUpdated && (
                <p className="text-[12px] font-medium leading-normal text-[#72777B]">
                  Last updated: {lastUpdated}
                </p>
              )}
            </>
          ) : (
            <p className="text-[12px] font-medium leading-normal text-[#72777B]">
              No exchange rate has been set yet. Enter one below to activate it.
            </p>
          )}
        </div>

        <div className="h-px bg-outline" />

        {/* New rate entry */}
        <div className="flex flex-col gap-4 p-4">
          <p className="text-[12px] font-semibold leading-normal text-primary_text">
            New Exchange Rate
          </p>
          <div
            className={`flex items-center gap-2.5 bg-white border-[1.5px] rounded-xl px-[18px] py-4 ${
              error ? "border-negative" : "border-p_primary"
            }`}
          >
            <label
              htmlFor="exchange_rate"
              className="text-[14px] font-medium text-[#72777B] shrink-0"
            >
              1 {fromCurrency} =
            </label>
            <input
              id="exchange_rate"
              name="exchange_rate"
              data-testid="exchange-rate-input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={value}
              onChange={handleChange}
              disabled={loading}
              aria-invalid={Boolean(error)}
              aria-describedby={showWarning ? "exchange-rate-warning" : undefined}
              className="flex-1 min-w-0 bg-transparent text-[16px] font-bold text-primary_text outline-none"
            />
            <span className="text-[13px] font-semibold text-p_primary shrink-0">
              {toCurrency}
            </span>
          </div>
          {error && <ErrorMessage errorMessage={error} />}

          {/* Deviation warning — advisory only; the save is never blocked. */}
          {showWarning && deviation !== null && activeRate && (
            <div
              id="exchange-rate-warning"
              role="alert"
              data-testid="exchange-rate-deviation-warning"
              className="flex items-start gap-3 bg-[#FFFBEB] rounded-[10px] p-2"
            >
              <span className="text-[15px] font-bold leading-normal text-[#D97706] shrink-0" aria-hidden="true">
                &#9888;
              </span>
              <p className="flex-1 min-w-0 text-[12px] font-medium leading-[18px] text-[#92400E]">
                Rate deviates {Math.abs(deviation).toFixed(2)}% from previous value (
                {activeRate.rate}).
              </p>
            </div>
          )}

          <div className="bg-[#E5F0FC] rounded-lg p-2">
            <p className="text-[12px] font-medium leading-5 tracking-[0.06px] text-primary_text">
              Note: Applies to all card transactions immediately.
            </p>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default ExchangeRateModal;
